/**
 * Airport briefing: overview, aerodrome chart, runways, SID and STAR charts, sources. Drawn from
 * AirportData, plus the OSM extract for taxiways and aprons; schematic, not for navigation.
 * Used as a page from the main menu and as an overlay in a session, where a procedure can also be
 * drawn on the radar scope.
 */
import { approachPath, defaultApproach, destination, findRunwayEnd, NM_TO_M, toMagneticDeg, type AirportData, type InstrumentApproach, type LatLon, type Procedure } from '@atc/core';
import { AIRPORTS, airportEntry } from '../airports';
import { hdg3 } from '../hints';
import { mountMenuNavigation } from '../menu-navigation';
import { CHART_STYLE, esc, fitFrame, northArrow, scaleBar } from './chart';
import { formatRestriction, procedureGeometry, proceduresByRunway, runwayOfKey } from './procedures';

export interface BriefingOptions {
  onClose(): void;
  closeLabel?: string;
  /** In a session: draw the selected procedure on the radar scope (null clears). */
  onShowOnScope?(lines: LatLon[][] | null): void;
  /** Links to the other airports' briefings (the menu page). */
  airportLinks?: boolean;
}

type Tab = 'overview' | 'aerodrome' | 'runways' | 'sid' | 'star' | 'approach' | 'sources';
const TABS: [Tab, string][] = [['overview', 'Overview'], ['aerodrome', 'Aerodrome'], ['runways', 'Runways'], ['sid', 'SIDs'], ['star', 'STARs'], ['approach', 'Approaches'], ['sources', 'Sources']];

const W = 1000, H = 680;
const num = (n: number) => Math.round(n).toLocaleString('en-US');

interface OsmFeature { properties: Record<string, string | undefined>; geometry: { type: string; coordinates: any } }
interface OsmExtract { features: OsmFeature[] }

const osmCache = new Map<string, Promise<OsmExtract | null>>();
function loadOsm(icao: string): Promise<OsmExtract | null> {
  const key = icao.toLowerCase();
  if (!osmCache.has(key)) osmCache.set(key, fetch(`/data/${key}/osm.geojson`).then((r) => (r.ok ? r.json() : null)).catch(() => null));
  return osmCache.get(key)!;
}

export function showBriefing(root: HTMLElement, airport: AirportData, o: BriefingOptions): () => void {
  const entry = airportEntry(airport.icao);
  const magnetic = (trueDeg: number) => hdg3(toMagneticDeg(airport, trueDeg));
  const variation = `${Math.abs(airport.magneticNorthDeg).toFixed(1)}° ${airport.magneticNorthDeg >= 0 ? 'east' : 'west'}`;
  const wind = airport.wind.speedKt ? `${magnetic(airport.wind.directionDeg)}° at ${Math.round(airport.wind.speedKt)} kt` : 'calm';
  const el = document.createElement('div');
  el.className = 'briefing';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');
  el.setAttribute('aria-label', `${airport.icao} briefing`);
  el.innerHTML = `
    <header class="brief-head">
      <div>
        <h1>${airport.icao} <span>${esc(entry?.name ?? airport.name)}</span></h1>
        <p>${esc(entry?.city ?? '')}${airport.iata ? `, IATA ${esc(airport.iata)}` : ''}</p>
      </div>
      ${o.airportLinks ? `<nav class="brief-airports" aria-label="Airports">${AIRPORTS.map((a) => `<a href="#/briefing/${a.icao}"${a.icao === airport.icao.toLowerCase() ? ' class="active" aria-current="page"' : ''}>${a.icao.toUpperCase()}</a>`).join('')}</nav>` : ''}
      <button class="btn" type="button" data-act="close">${esc(o.closeLabel ?? 'Close')}</button>
      <div class="brief-plate-row"><dl class="brief-plate">
        <div><dt>Elevation</dt><dd>${num(airport.elevationFt)} ft</dd></div>
        <div><dt>Variation</dt><dd>${variation}</dd></div>
        <div><dt>Landing</dt><dd>${esc(airport.arrivalRunway)}</dd></div>
        <div><dt>Departing</dt><dd>${esc(airport.departureRunway)}</dd></div>
        <div><dt>Prevailing wind</dt><dd>${wind}</dd></div>
      </dl></div>
    </header>
    <div class="brief-tabs" role="tablist" aria-label="Briefing sections">${TABS.map(([id, label]) => `<button type="button" role="tab" data-tab="${id}" aria-selected="false">${label}</button>`).join('')}</div>
    <div class="brief-body" role="tabpanel"></div>`;
  root.appendChild(el);
  const body = el.querySelector<HTMLElement>('.brief-body')!;
  let disposed = false;
  let tab: Tab = 'overview';
  /** Selected procedure per tab, and the one currently drawn on the scope. */
  const selected: Partial<Record<'sid' | 'star', { p: Procedure; runway: string }>> = {};
  let onScope: string | null = null;

  function select(next: Tab): void {
    tab = next;
    for (const b of el.querySelectorAll<HTMLElement>('[data-tab]')) b.setAttribute('aria-selected', String(b.dataset.tab === next));
    body.scrollTop = 0;
    if (next === 'overview') overview();
    else if (next === 'aerodrome') aerodrome();
    else if (next === 'runways') runways();
    else if (next === 'sources') sources();
    else if (next === 'approach') approaches();
    else procedures(next);
  }

  let approachSel: string | null = null;
  /** Published approaches, plus the synthesized one for the landing runway when it has none. */
  function approachList(): InstrumentApproach[] {
    const list = [...airport.approaches];
    if (!list.some((a) => a.runway === airport.arrivalRunway)) { const a = defaultApproach(airport, airport.arrivalRunway); if (a) list.push(a); }
    return list.sort((a, b) => a.runway.localeCompare(b.runway, undefined, { numeric: true }) || a.name.localeCompare(b.name));
  }

  function approaches(): void {
    const list = approachList();
    if (!list.length) { body.innerHTML = `<p class="brief-note">No approaches in the ${airport.icao} data.</p>`; return; }
    const cur = list.find((a) => a.id === approachSel) ?? list.find((a) => a.runway === airport.arrivalRunway) ?? list[0]!;
    approachSel = cur.id;
    const key = `approach:${cur.id}`;
    const synthesized = cur.source === 'synthesized';
    const thrHdg = findRunwayEnd(airport, cur.runway)?.headingDeg ?? 0;
    const missed = (cur.missed ?? [{ kind: 'heading' as const, headingDeg: thrHdg }]).map((l) => (l.kind === 'fix' ? l.fix : `heading ${magnetic(l.headingDeg)}`)).join(', then ');
    const rows = cur.legs.map((l) => `<tr><td>${esc(l.role ?? '')}</td><td>${synthesized ? `${esc(l.role ?? '')} (computed)` : esc(l.fix)}</td><td>${esc(formatRestriction(l.restriction) || '—')}</td></tr>`).join('');
    const runwaysListed = [...new Set(list.map((a) => a.runway))];
    body.innerHTML = `<div class="brief-procs">
      <nav class="brief-list" aria-label="Approaches">${runwaysListed.map((rw) => `<section><h3>Runway ${esc(rw)}${rw === airport.arrivalRunway ? ' (landing)' : ''}</h3>${list.filter((a) => a.runway === rw).map((a) => `<button type="button" data-approach="${esc(a.id)}" aria-pressed="${a === cur}">${esc(a.name)}</button>`).join('')}</section>`).join('')}</nav>
      <div class="brief-proc">
        <div class="brief-proc-head">
          <h2>${esc(cur.name)}</h2>
          <span class="brief-muted">${cur.navaid ? `Localizer ${esc(cur.navaid.ident)} ${cur.navaid.frequencyMHz.toFixed(1)} MHz` : synthesized ? 'Synthesized straight-in approach: no published data' : ''}</span>
          ${o.onShowOnScope ? `<button class="btn" type="button" data-act="scope-approach" data-key="${esc(key)}">${onScope === key ? 'Remove from scope' : 'Show on scope'}</button>` : ''}
        </div>
        <div class="brief-scroll"><table class="brief-table"><thead><tr><th>Role</th><th>Fix</th><th>Altitude and speed</th></tr></thead><tbody>${rows}</tbody></table></div>
        <p class="brief-note">Missed approach: ${esc(missed)}. Altitudes in feet, headings true.${synthesized ? ' The final approach point is where the glideslope reaches 2000 ft above the threshold, rounded up to 500 ft; the intermediate fix is 4 nm before it.' : ''}</p>
      </div>
    </div>`;
  }

  function overview(): void {
    const groups: [string, [string, string][]][] = [
      ['Radio', [
        ['Tower', airport.radio.twr],
        ['Approach', airport.radio.app],
        ['Departure', airport.radio.dep],
      ]],
      ['Runways', [
        ['Runways', airport.runways.map((r) => r.name).join(', ')],
        ['Arrivals land on', `runway ${airport.arrivalRunway}`],
        ['Departures take off from', `runway ${airport.departureRunway}`],
        ['Departures first cleared to', `${num(airport.initialAltitudeFt)} ft`],
        ['Procedures', `${airport.sids.size} SIDs, ${airport.stars.size} STARs`],
      ]],
      ['Field', [
        ['Airport', `${entry?.name ?? airport.name} (${airport.icao}${airport.iata ? ` / ${airport.iata}` : ''})`],
        ['Elevation', `${num(airport.elevationFt)} ft`],
        ['Magnetic variation', variation],
        ['Prevailing wind (weather scales its speed; none at low realism)', wind],
        ['Time zone', airport.xAtc.timezone ?? 'not recorded'],
        ['AIRAC cycle', airport.airac ? String(airport.airac) : 'not recorded'],
      ]],
    ];
    body.innerHTML = `<div class="brief-groups">${groups.map(([title, rows]) => `<section class="plate-group"><h2>${title}</h2><dl class="brief-dl">${rows.map(([k, v]) => `<dt>${k}</dt><dd>${esc(v)}</dd>`).join('')}</dl></section>`).join('')}</div>
      <p class="brief-note">Charts in this briefing are drawn from the game's airport data. They are schematic and not for navigation.</p>`;
  }

  function runways(): void {
    const rows = airport.runways.flatMap((r) => r.ends.map((e) => {
      const use = [e.name === airport.arrivalRunway ? 'arrivals' : '', e.name === airport.departureRunway ? 'departures' : ''].filter(Boolean).join(', ');
      return `<tr><td>${esc(e.name)}</td><td>${esc(r.name)}</td><td>${hdg3(e.headingDeg)}°</td><td>${magnetic(e.headingDeg)}°</td><td>${num(r.lengthM)} m (${num(r.lengthM / 0.3048)} ft)</td><td>${e.ils ? `ILS, ${e.glideslopeDeg.toFixed(1)}° glideslope` : 'none'}</td><td>${num(e.elevationM / 0.3048)} ft</td><td>${use || '—'}</td></tr>`;
    }));
    body.innerHTML = `<div class="brief-scroll"><table class="brief-table">
      <thead><tr><th>End</th><th>Runway</th><th>True heading</th><th>Magnetic</th><th>Length</th><th>Approach aid</th><th>Threshold</th><th>In use for</th></tr></thead>
      <tbody>${rows.join('')}</tbody></table></div>
      <p class="brief-note">Headings you say, read and see are magnetic, as on the charts (true minus ${airport.magneticNorthDeg}° variation); runway numbers follow them.</p>`;
  }

  function sources(): void {
    const src = airport.xAtc.sources ?? [];
    const notes = airport.xAtc.notes ?? [];
    body.innerHTML = `
      ${src.length
        ? `<h2 class="brief-h2">Official sources</h2><ul class="brief-sources">${src.map((s) => `<li>${s.url ? `<a href="${esc(s.url)}" target="_blank" rel="noopener">${esc(s.item)}</a>` : esc(s.item)}${s.date ? ` <span class="brief-muted">(${esc(s.date)})</span>` : ''}${s.note ? `<br><span class="brief-muted">${esc(s.note)}</span>` : ''}</li>`).join('')}</ul>`
        : `<p class="brief-note">No official sources are recorded for ${airport.icao}. Its data comes from the openScope project.</p>`}
      ${notes.length ? `<h2 class="brief-h2">Notes</h2><ul class="brief-sources">${notes.map((n) => `<li>${esc(n)}</li>`).join('')}</ul>` : ''}`;
  }

  function aerodrome(): void {
    body.innerHTML = `<p class="brief-note">Loading the airfield map</p>`;
    void loadOsm(airport.icao).then((osm) => {
      if (disposed || tab !== 'aerodrome') return;
      body.innerHTML = `${aerodromeChart(osm)}${osm ? '' : '<p class="brief-note">No airfield map for this airport; runways only.</p>'}`;
    });
  }

  function aerodromeChart(osm: OsmExtract | null): string {
    const feats = osm?.features ?? [];
    const ring = (coords: [number, number][]): LatLon[] => coords.map(([lon, lat]) => ({ lat, lon }));
    const byAero = (t: string) => feats.filter((f) => f.properties.aeroway === t);
    const ad = byAero('aerodrome').find((f) => f.geometry.type === 'Polygon');
    const bounds: LatLon[] = airport.runways.flatMap((r) => r.ends.map((e) => ({ lat: e.lat, lon: e.lon })));
    if (ad) bounds.push(...ring(ad.geometry.coordinates[0]));
    if (airport.xAtc.tower) bounds.push(airport.xAtc.tower);
    const f = fitFrame(bounds, W, H, 60);
    const mPx = f.pxPerNm / NM_TO_M;
    const pt = (q: LatLon) => `${f.x(q.lat, q.lon).toFixed(1)},${f.y(q.lat, q.lon).toFixed(1)}`;
    const poly = (coords: [number, number][], cls: string) => `<path d="M${ring(coords).map(pt).join('L')}Z" class="${cls}"/>`;
    const polys = (t: string, cls: string) => byAero(t).flatMap((x) => x.geometry.type === 'Polygon' ? [poly(x.geometry.coordinates[0], cls)] : x.geometry.type === 'MultiPolygon' ? (x.geometry.coordinates as [number, number][][][]).map((p) => poly(p[0]!, cls)) : []).join('');
    // Assumption: taxiways drawn 23 m wide, runways 45 m (the tower view's defaults), never thinner than a hairline.
    const taxiways = byAero('taxiway').filter((x) => x.geometry.type === 'LineString').map((x) => `<path d="M${ring(x.geometry.coordinates).map(pt).join('L')}" class="bc-twy" stroke-width="${Math.max(1.2, 23 * mPx).toFixed(1)}"/>`).join('');
    const rwyW = Math.max(3, 45 * mPx);
    const runwaysSvg = airport.runways.map((r) => `<line x1="${f.x(r.ends[0].lat, r.ends[0].lon).toFixed(1)}" y1="${f.y(r.ends[0].lat, r.ends[0].lon).toFixed(1)}" x2="${f.x(r.ends[1].lat, r.ends[1].lon).toFixed(1)}" y2="${f.y(r.ends[1].lat, r.ends[1].lon).toFixed(1)}" stroke="#e8edf2" stroke-width="${rwyW.toFixed(1)}"/>`).join('');
    const labels = airport.runways.flatMap((r) => r.ends.map((e) => {
      const at = destination(e.lat, e.lon, (e.headingDeg + 180) % 360, Math.max(250, 30 / mPx));
      const x = f.x(at.lat, at.lon), y = f.y(at.lat, at.lon);
      return `<text x="${x.toFixed(1)}" y="${y.toFixed(1)}" text-anchor="middle" class="bc-rw">${esc(e.name)}</text><text x="${x.toFixed(1)}" y="${(y + 14).toFixed(1)}" text-anchor="middle" class="bc-rwm">${magnetic(e.headingDeg)}°${e.ils ? ' ILS' : ''}</text>`;
    })).join('');
    const t = airport.xAtc.tower;
    const tower = t ? `<g transform="translate(${pt(t)})"><circle r="6" fill="#05080c" stroke="#5cc8ff" stroke-width="2"/><text x="10" y="4" class="bc-fx">TWR</text></g>` : '';
    return `<svg class="brief-chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="Aerodrome chart of ${airport.icao}">${CHART_STYLE}
      <rect width="${W}" height="${H}" fill="#070b10"/>
      ${ad ? poly(ad.geometry.coordinates[0], 'bc-ad') : ''}${polys('apron', 'bc-apron')}${taxiways}${polys('terminal', 'bc-bldg')}${polys('hangar', 'bc-bldg')}
      ${runwaysSvg}${labels}${tower}
      <text x="24" y="36" class="bc-ttl">${airport.icao} aerodrome</text>
      ${northArrow(W - 50, 70, airport.magneticNorthDeg)}${scaleBar(f, 30, H - 30)}
      <text x="${W - 24}" y="${H - 18}" text-anchor="end" class="bc-ft">${osm ? 'Taxiways, aprons and buildings © OpenStreetMap contributors. ' : ''}Schematic, not for navigation</text>
    </svg>`;
  }

  function procedures(kind: 'sid' | 'star'): void {
    const groups = proceduresByRunway(airport).map((g) => ({ runway: g.runway, list: kind === 'sid' ? g.sids : g.stars })).filter((g) => g.list.length);
    const label = kind === 'sid' ? 'SIDs' : 'STARs';
    if (!groups.length) { body.innerHTML = `<p class="brief-note">No ${label} in the ${airport.icao} data.</p>`; return; }
    if (!selected[kind]) {
      const inUse = kind === 'sid' ? airport.departureRunway : airport.arrivalRunway;
      const g = groups.find((x) => x.runway === inUse) ?? groups[0]!;
      selected[kind] = { p: g.list[0]!, runway: g.runway };
    }
    const cur = selected[kind]!;
    body.innerHTML = `<div class="brief-procs">
      <nav class="brief-list" aria-label="${label}">${groups.map((g) => `<section><h3>${g.runway ? `Runway ${esc(g.runway)}` : 'Any runway'}</h3>${g.list.map((p) => `<button type="button" data-proc="${esc(p.icao)}" data-runway="${esc(g.runway)}" aria-pressed="${p === cur.p && g.runway === cur.runway}">${esc(p.name)}</button>`).join('')}</section>`).join('')}</nav>
      <div class="brief-proc">${procedureDetail(cur.p, cur.runway)}</div>
    </div>`;
  }

  function procedureDetail(p: Procedure, runway: string): string {
    const rwyKey = runway && p.rwy[`${airport.icao}${runway}`] ? `${airport.icao}${runway}` : Object.keys(p.rwy)[0];
    const rwName = rwyKey ? runwayOfKey(airport, rwyKey) : undefined;
    const chart = procedureChart(p, rwyKey);
    const key = `${p.kind}:${p.icao}:${runway}`;
    return `<div class="brief-proc-head">
        <h2>${esc(p.name)}</h2>
        <span class="brief-muted">${p.kind === 'sid' ? 'Departure' : 'Arrival'}${rwName ? `, runway ${esc(rwName)}` : ''}</span>
        ${o.onShowOnScope ? `<button class="btn" type="button" data-act="scope" data-key="${esc(key)}">${onScope === key ? 'Remove from scope' : 'Show on scope'}</button>` : ''}
      </div>
      ${chart.svg}
      <div class="brief-scroll"><table class="brief-table"><thead><tr><th>Fix or heading</th><th>Restriction</th><th>Part</th></tr></thead><tbody>${chart.rows.join('')}</tbody></table></div>`;
  }

  function procedureChart(p: Procedure, rwyKey: string | undefined): { svg: string; rows: string[]; lines: LatLon[][] } {
    const rwName = rwyKey ? runwayOfKey(airport, rwyKey) : undefined;
    const threshold = rwName ? findRunwayEnd(airport, rwName) : undefined;
    const geo = procedureGeometry(airport, p, rwyKey);
    const drawn = geo.lines.map((l) => ({ pts: l.points, main: l.emphasis === 'main' }));
    const fixes = new Map(geo.fixes.map((f) => [f.name, { at: f.at, restriction: formatRestriction(f.restriction) }] as const));
    const headings = geo.headings.map((h) => ({ at: h.at, text: `hdg ${magnetic(h.headingDeg)}` }));
    const rows = geo.rows.map((r) => {
      const part = r.transition ? `${esc(r.transition)} transition` : 'main route';
      return 'fix' in r ? `<tr><td>${esc(r.fix)}</td><td>${esc(r.restriction || '—')}</td><td>${part}</td></tr>` : `<tr><td>heading ${magnetic(r.headingDeg)}</td><td>—</td><td>${part}</td></tr>`;
    });
    const bounds = [...drawn.flatMap((d) => d.pts), { lat: airport.lat, lon: airport.lon }];
    const f = fitFrame(bounds, W, H, 80);
    const xy = (q: LatLon) => `${f.x(q.lat, q.lon).toFixed(1)},${f.y(q.lat, q.lon).toFixed(1)}`;
    const path = (pts: LatLon[]) => `M${pts.map(xy).join('L')}`;
    const runwaysSvg = airport.runways.map((r) => `<path d="${path([r.ends[0], r.ends[1]])}" stroke="#e8edf2" stroke-width="4"/>`).join('');
    const lines = [...drawn].sort((a, b) => Number(a.main) - Number(b.main)).map((d) => `<path d="${path(d.pts)}" fill="none" stroke="${d.main ? '#5cc8ff' : '#7f98ad'}" stroke-width="${d.main ? 3 : 1.6}"${d.main ? '' : ' stroke-dasharray="7 5"'} stroke-linejoin="round"/>`).join('');
    const fixSvg = [...fixes].map(([name, v]) => `<g transform="translate(${xy(v.at)})"><path d="M0,-6L5.5,4H-5.5Z" fill="#05080c" stroke="#9fd39f" stroke-width="1.5"/><text x="9" y="4" class="bc-fx">${esc(name)}</text>${v.restriction ? `<text x="9" y="18" class="bc-rs">${esc(v.restriction)}</text>` : ''}</g>`).join('');
    const hdgSvg = headings.map((h) => `<text x="${(f.x(h.at.lat, h.at.lon) + 6).toFixed(1)}" y="${(f.y(h.at.lat, h.at.lon) - 6).toFixed(1)}" class="bc-hd">${h.text}</text>`).join('');
    const rwLabel = threshold ? `<text x="${(f.x(threshold.lat, threshold.lon) + 8).toFixed(1)}" y="${(f.y(threshold.lat, threshold.lon) + 16).toFixed(1)}" class="bc-rw">RWY ${esc(threshold.name)}</text>` : '';
    const svg = `<svg class="brief-chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="${esc(p.name)} chart">${CHART_STYLE}
      <rect width="${W}" height="${H}" fill="#070b10"/>
      ${runwaysSvg}${lines}${fixSvg}${hdgSvg}${rwLabel}
      <text x="24" y="36" class="bc-ttl">${esc(p.name)}</text>
      <text x="24" y="56" class="bc-sb">Bold: ${p.kind === 'sid' ? 'runway and common route' : 'common route to the runway'}. Dashed: ${p.kind === 'sid' ? 'exit' : 'entry'} transitions. Altitudes in feet.</text>
      ${northArrow(W - 50, 70, airport.magneticNorthDeg)}${scaleBar(f, 30, H - 30)}
      <text x="${W - 24}" y="${H - 18}" text-anchor="end" class="bc-ft">Schematic, not for navigation</text>
    </svg>`;
    return { svg, rows, lines: drawn.map((d) => d.pts) };
  }

  el.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    const tabBtn = t.closest<HTMLElement>('[data-tab]');
    if (tabBtn) { select(tabBtn.dataset.tab as Tab); return; }
    const proc = t.closest<HTMLElement>('[data-proc]');
    if (proc && (tab === 'sid' || tab === 'star')) {
      const p = (tab === 'sid' ? airport.sids : airport.stars).get(proc.dataset.proc!);
      if (p) { selected[tab] = { p, runway: proc.dataset.runway ?? '' }; procedures(tab); }
      return;
    }
    const ap = t.closest<HTMLElement>('[data-approach]');
    if (ap && tab === 'approach') { approachSel = ap.dataset.approach!; approaches(); return; }
    const act = t.closest<HTMLElement>('[data-act]');
    if (act?.dataset.act === 'scope-approach' && o.onShowOnScope) {
      const key = act.dataset.key!;
      if (onScope === key) { onScope = null; o.onShowOnScope(null); }
      else {
        const a = approachList().find((x) => `approach:${x.id}` === key);
        const path = a ? approachPath(airport, a) : undefined;
        if (path) { onScope = key; o.onShowOnScope([[...path.points, path.threshold], path.missed]); }
      }
      approaches();
      return;
    }
    if (act?.dataset.act === 'close') o.onClose();
    else if (act?.dataset.act === 'scope' && o.onShowOnScope && (tab === 'sid' || tab === 'star')) {
      const cur = selected[tab]!;
      const key = act.dataset.key!;
      if (onScope === key) { onScope = null; o.onShowOnScope(null); }
      else {
        const rwyKey = cur.runway && cur.p.rwy[`${airport.icao}${cur.runway}`] ? `${airport.icao}${cur.runway}` : Object.keys(cur.p.rwy)[0];
        onScope = key;
        o.onShowOnScope(procedureChart(cur.p, rwyKey).lines);
      }
      procedures(tab);
    }
  });
  const disposeNavigation = mountMenuNavigation(el, o.onClose);
  select('overview');
  el.querySelector<HTMLElement>('[data-tab="overview"]')?.focus();
  return () => { disposed = true; disposeNavigation(); el.remove(); };
}
